/**
 * Single, typed entry point for build-time configuration.
 * Never read `import.meta.env` outside this module.
 */

type RawEnv = {
  VITE_APP_NAME?: string
  VITE_API_URL?: string
  VITE_API_TIMEOUT_MS?: string
  VITE_REQUIRE_AUTH?: string
  VITE_ENTRA_TENANT_ID?: string
  VITE_ENTRA_CLIENT_ID?: string
  VITE_ENTRA_API_SCOPE?: string
}

const raw = import.meta.env as unknown as RawEnv

function required(value: string | undefined, key: keyof RawEnv, fallback?: string): string {
  const resolved = value ?? fallback
  if (resolved === undefined || resolved === '') {
    throw new Error(`Missing required environment variable: ${key}`)
  }
  return resolved
}

function boolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback
  return value === 'true' || value === '1'
}

function integer(value: string | undefined, fallback: number): number {
  const parsed = Number.parseInt(value ?? '', 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

const requireAuth = boolean(raw.VITE_REQUIRE_AUTH, false)

/**
 * Entra values are only required when auth is actually enforced — local dev
 * with `requireAuth` off never needs a real Entra app registration.
 */
function entraValue(value: string | undefined, key: keyof RawEnv): string {
  return requireAuth ? required(value, key) : (value ?? '')
}

export const env = {
  appName: required(raw.VITE_APP_NAME, 'VITE_APP_NAME', 'TQ Capacity'),
  apiUrl: required(raw.VITE_API_URL, 'VITE_API_URL', '/api'),
  apiTimeoutMs: integer(raw.VITE_API_TIMEOUT_MS, 15_000),
  /**
   * When false, route guards pass everyone through and a stand-in user is seeded.
   * The auth feature stays wired up — flip this to true to bring sign-in back.
   */
  requireAuth,
  entraTenantId: entraValue(raw.VITE_ENTRA_TENANT_ID, 'VITE_ENTRA_TENANT_ID'),
  entraClientId: entraValue(raw.VITE_ENTRA_CLIENT_ID, 'VITE_ENTRA_CLIENT_ID'),
  /** The full `api://<client-id>/access_as_user` string from "Expose an API". */
  entraApiScope: entraValue(raw.VITE_ENTRA_API_SCOPE, 'VITE_ENTRA_API_SCOPE'),
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const

export type Env = typeof env
